import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, Phone, Mail, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const sectorHint = searchParams.get('sector');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Pre-fill message when user arrives from a sector page
  useEffect(() => {
    if (!sectorHint) return;
    setFormData((prev) => {
      if (prev.message) return prev; // don't overwrite if user has already typed
      const prefix = language === 'fr'
        ? `[Secteur : ${sectorHint}]\n\nBonjour, je souhaite discuter d'un projet dans ce secteur.\n\n`
        : `[Sector: ${sectorHint}]\n\nHello, I'd like to discuss a project in this sector.\n\n`;
      return { ...prev, message: prefix };
    });
  }, [sectorHint, language]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        language: language
      });
      
      setSubmitted(true);
      toast.success(
        language === 'fr' 
          ? 'Message envoyé avec succès!'
          : 'Message sent successfully!'
      );
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
      
      setTimeout(() => setSubmitted(false), 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(
        language === 'fr' 
          ? 'Erreur lors de l\'envoi. Veuillez réessayer.'
          : 'Error sending message. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <section 
        className="relative h-96 flex items-center justify-center overflow-hidden"
        data-testid="contact-hero"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1581374820531-029275791beb?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-primary/80 to-primary/95"></div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.h1 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-serif text-4xl sm:text-5xl font-bold mb-4"
          >
            {language === 'fr' ? 'Contactez-nous' : 'Contact Us'}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg max-w-2xl mx-auto opacity-90"
          >
            {language === 'fr' 
              ? 'Nous sommes là pour répondre à vos questions'
              : 'We\'re here to answer your questions'}
          </motion.p>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background" data-testid="contact-content">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-serif text-3xl font-bold mb-6 text-foreground">
                {language === 'fr' ? 'Informations de Contact' : 'Contact Information'}
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="h-6 w-6 text-secondary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1 text-foreground">
                      {language === 'fr' ? 'Adresse' : 'Address'}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      #42 Immeuble Papa Victor<br />
                      Local 11, Avenue Tombalbaye<br />
                      Commune de la Gombe<br />
                      Kinshasa, R.D. Congo
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Phone className="h-6 w-6 text-secondary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1 text-foreground">
                      {language === 'fr' ? 'Téléphone' : 'Phone'}
                    </h3>
                    <p className="text-muted-foreground">
                      <a href="tel:+243979097660" className="hover:text-primary transition-colors">+243 97 90 97 660</a>
                    </p>
                    <p className="text-muted-foreground">
                      <a href="tel:+243827777182" className="hover:text-primary transition-colors">+243 82 77 77 182</a>
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Mail className="h-6 w-6 text-secondary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1 text-foreground">
                      {language === 'fr' ? 'Email' : 'Email'}
                    </h3>
                    <p className="text-muted-foreground">
                      <a href="mailto:info@fbgroup.cd" className="hover:text-primary transition-colors">info@fbgroup.cd</a>
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-8 bg-primary text-white">
                <h3 className="font-serif text-xl font-bold mb-4">
                  {language === 'fr' ? 'Heures d\'Ouverture' : 'Business Hours'}
                </h3>
                <div className="space-y-2 text-sm">
                  <p>{language === 'fr' ? 'Lundi - Vendredi' : 'Monday - Friday'}: 8:00 AM - 5:00 PM</p>
                  <p>{language === 'fr' ? 'Samedi' : 'Saturday'}: 9:00 AM - 1:00 PM</p>
                  <p>{language === 'fr' ? 'Dimanche' : 'Sunday'}: {language === 'fr' ? 'Fermé' : 'Closed'}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-serif text-3xl font-bold mb-6 text-foreground">
                {language === 'fr' ? 'Envoyez-nous un Message' : 'Send us a Message'}
              </h2>
              
              {submitted ? (
                <div className="bg-accent/20 border border-accent p-8 text-center" data-testid="success-message">
                  <CheckCircle2 className="h-16 w-16 text-primary mx-auto mb-4" />
                  <h3 className="font-serif text-xl font-bold mb-2 text-foreground">
                    {language === 'fr' ? 'Merci!' : 'Thank You!'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'fr' 
                      ? 'Nous avons reçu votre message et nous vous répondrons bientôt.'
                      : 'We\'ve received your message and will respond soon.'}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                      {language === 'fr' ? 'Nom' : 'Name'} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      data-testid="name-input"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-primary/20 focus:border-primary px-0 py-3 text-base focus:outline-none transition-colors placeholder:text-muted-foreground/50"
                      placeholder={language === 'fr' ? 'Votre nom' : 'Your name'}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      data-testid="email-input"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-primary/20 focus:border-primary px-0 py-3 text-base focus:outline-none transition-colors placeholder:text-muted-foreground/50"
                      placeholder={language === 'fr' ? 'votre@email.com' : 'your@email.com'}
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                      {language === 'fr' ? 'Téléphone' : 'Phone'}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      data-testid="phone-input"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-primary/20 focus:border-primary px-0 py-3 text-base focus:outline-none transition-colors placeholder:text-muted-foreground/50"
                      placeholder="+243 XXX XXX XXX"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-foreground mb-2">
                      {language === 'fr' ? 'Entreprise' : 'Company'}
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      data-testid="company-input"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-primary/20 focus:border-primary px-0 py-3 text-base focus:outline-none transition-colors placeholder:text-muted-foreground/50"
                      placeholder={language === 'fr' ? 'Nom de votre entreprise' : 'Your company name'}
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      data-testid="message-input"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full bg-transparent border border-primary/20 focus:border-primary p-4 text-base focus:outline-none transition-colors placeholder:text-muted-foreground/50 resize-none"
                      placeholder={language === 'fr' ? 'Votre message...' : 'Your message...'}
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="submit-btn"
                    disabled={loading}
                    className="w-full btn-primary inline-flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>{language === 'fr' ? 'Envoi...' : 'Sending...'}</span>
                      </>
                    ) : (
                      <>
                        <span>{language === 'fr' ? 'Envoyer' : 'Send Message'}</span>
                        <Send className="h-5 w-5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;